import { Box, Stack } from '@mui/material';
import React, { useState } from 'react';

import ExpensesRow from './expenses-row';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import IOption from '@models/option';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import JDatePicker from '@atoms/JDatePicker';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { appendSearchFilter } from '@lib/tableFilterUtils';
import { useGetConsumptions } from '@/modules/credits/queries';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetEstablishmentsSuggestions } from '@establishment/queries/useGetEstablishment';
import { useTranslation } from 'react-i18next';

const CONSUMPTIONS_PER_PAGE = 20;

const TABLE_COLS: TableColumn[] = [
  { name: 'ENTERPRISE', orderable: true, sortingField: 'enterprise.name' },
  { name: 'ESTABLISHMENT', orderable: true, sortingField: 'enterprise.siren' },
  {
    name: 'USER',
    orderable: true,
    sortingField: 'creditConsumption.created_by',
  },
  {
    name: 'ACTION',
    orderable: true,
    sortingField: 'creditConsumption.consumption_action',
  },
  {
    name: 'CREDITS_CONSUMED',
    orderable: true,
    sortingField: 'creditConsumption.credits_amount_consumed',
  },
  {
    name: 'DATE_CONSUMPTION',
    orderable: true,
    sortingField: 'creditConsumption.created_at',
  },
];

const DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'enterprise.deleted_at',
};

const ExpencesList: React.FC = () => {
  const { t } = useTranslation();

  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(DEFAULT_ORDER);
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');
  const [page, setPage] = useState<number>();

  const [establishmentInputValue, setEstablishmentInputValue] =
    useState<string>('');

  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);

  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } = useGetEstablishmentsSuggestions(
    10,
    1,
    establishmentInputValue,
  );

  const { data: dataConsumptions } = useGetConsumptions(
    {
      page,
      limit: CONSUMPTIONS_PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const meta = dataConsumptions?.data.meta;

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JAutocomplete
          value={{ label: establishmentInputValue, value: '' }}
          label={t('ESTABLISHMENT')}
          options={establishmentsOptions}
          onChange={(ev, newValue) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_ESTABLISHMENT_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            );
            setEstablishmentInputValue((newValue as IOption)?.label || '');
          }}
          onTextChange={(val) => setEstablishmentInputValue(val)}
          whiteBg
        />
        <JAutocomplete
          value={{ label: enterpriseInputValue, value: '' }}
          label={t('ENTERPRISE_NAME')}
          options={enterprisesOptions}
          onChange={(ev, newValue) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_ENTERPRISE_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            );
            setEnterpriseInputValue((newValue as IOption)?.label || '');
          }}
          onTextChange={(val) => setEnterpriseInputValue(val)}
          whiteBg
        />
        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_CREATED_AT_FROM,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_CREATED_AT_FROM,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('START_DATE')}
        />
        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_CREATED_AT_TO,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_CONSUMPTION_CREATED_AT_TO,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('END_DATE')}
        />
      </Stack>
      <JTable
        isNotFound={dataConsumptions?.data.meta.itemCount === 0}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_EXPENSES_FOUND') ?? ''}
        total={dataConsumptions?.data.meta.itemCount}
        perpage={CONSUMPTIONS_PER_PAGE}
        nbpages={dataConsumptions?.data.meta.pageCount}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {dataConsumptions?.data.data.map((consumption) => (
          <ExpensesRow
            row={consumption}
            key={consumption.creditConsumption_id}
          />
        ))}
      </JTable>
      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default ExpencesList;
